import { useRef, useState, useEffect } from "react";
import { useAuth } from "../../hooks/AuthProvider";
import { useNavigate } from "react-router-dom";

const UserIcon = () => {
  const { currentUser, logout } = useAuth();
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const toggleUserDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  // Add click away listener
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
    };

    // Add event listener when dropdown is open
    if (isUserDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Clean up
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isUserDropdownOpen]);

  const handleSignIn = () => {
    navigate('/login');
  };

  const handleSignOut = async () => {
    try {
      await logout();
      setIsUserDropdownOpen(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleAccountSettings = () => {
    navigate('/account'); // Using navigate for SPA navigation
    setIsUserDropdownOpen(false);
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (currentUser?.displayName) {
      const nameParts = currentUser.displayName.split(' ');
      
      if (nameParts.length === 1) {
        // If there's only one name, use its first letter
        return nameParts[0][0].toUpperCase();
      } else {
        // Use first letter of first name and first letter of last name
        const firstName = nameParts[0];
        const lastName = nameParts[nameParts.length - 1];
        return (firstName[0] + lastName[0]).toUpperCase();
      }
    }
    
    if (currentUser?.email) {
      return currentUser.email[0].toUpperCase();
    }
    
    return '?';
  };

  // Inline styles
  const styles = {
    authSection: {
      display: "flex",
      alignItems: "center",
      marginLeft: "20px"
    },
    signInButton: {
      backgroundColor: "#4F46E5",
      color: "white",
      border: "none",
      padding: "8px 16px",
      borderRadius: "6px",
      fontSize: "14px",
      fontWeight: 500,
      cursor: "pointer",
      transition: "background-color 0.2s ease"
    },
    signInButtonHover: {
      backgroundColor: "#4338ca"
    },
    userProfile: {
      position: "relative" as const
    },
    userAvatar: {
      width: "36px",
      height: "36px",
      borderRadius: "50%",
      backgroundColor: "#4F46E5",
      color: "white",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontWeight: 600,
      cursor: "pointer",
      userSelect: "none" as const,
      transition: "background-color 0.2s ease"
    },
    userDropdown: {
      position: "absolute" as const,
      top: "45px",
      right: "0",
      width: "240px",
      backgroundColor: "white",
      borderRadius: "8px",
      boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
      zIndex: 1000,
      overflow: "hidden",
      animation: "dropdown-fade-in 0.2s ease"
    },
    userInfo: {
      padding: "16px"
    },
    userName: {
      fontWeight: 600,
      color: "#1a202c",
      marginBottom: "4px",
      wordBreak: "break-word" as const
    },
    userEmail: {
      color: "#718096",
      fontSize: "14px",
      wordBreak: "break-word" as const
    },
    dropdownDivider: {
      height: "1px",
      backgroundColor: "#e2e8f0",
      margin: "0"
    },
    dropdownItem: {
      width: "100%",
      padding: "12px 16px",
      textAlign: "left" as const,
      background: "none",
      border: "none",
      color: "#4a5568",
      fontSize: "14px",
      cursor: "pointer",
      transition: "background-color 0.2s ease"
    },
    dropdownItemHover: {
      backgroundColor: "#f7fafc",
      color: "#1a202c"
    }
  };

  return (
    <div style={styles.authSection}>
      {currentUser ? (
        <div style={styles.userProfile} ref={userDropdownRef}>
          <div
            style={styles.userAvatar}
            onClick={toggleUserDropdown}
            title={currentUser.displayName || currentUser.email || 'User profile'}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = '#4338ca';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = '#4F46E5';
            }}
          >
            {getUserInitials()}
          </div>
          {isUserDropdownOpen && (
            <div style={styles.userDropdown}>
              <div style={styles.userInfo}>
                {currentUser.displayName && (
                  <div style={styles.userName}>{currentUser.displayName}</div>
                )}
                <div style={styles.userEmail}>{currentUser.email}</div>
              </div>
              <div style={styles.dropdownDivider}></div>
              <button
                style={styles.dropdownItem}
                onClick={handleAccountSettings}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f7fafc';
                  e.currentTarget.style.color = '#1a202c';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '';
                  e.currentTarget.style.color = '#4a5568';
                }}
              >
                Account Settings
              </button>
              <button
                style={styles.dropdownItem}
                onClick={handleSignOut}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = '#f7fafc';
                  e.currentTarget.style.color = '#1a202c';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = '';
                  e.currentTarget.style.color = '#4a5568';
                }}
              >
                Sign Out
              </button>
            </div>
          )}
        </div>
      ) : (
        <button
          style={styles.signInButton}
          onClick={handleSignIn}
          onMouseOver={(e) => {
            e.currentTarget.style.backgroundColor = '#4338ca';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = '#4F46E5';
          }}
        >
          Sign In
        </button>
      )}
    </div>
  );
};

export default UserIcon;